<template>
  <div>
    <ShareLinkInvalidMessage v-if="showLinkExpiredMessage">
      This link is either invalid, or the goal is no longer shared by its owner. Ask the owner of the goal for a new shareable link.
    </ShareLinkInvalidMessage>
    <template v-else>
      <CopyTrackWrapper
        :type="savedGoalContentType"
        :title="pageContent.title"
        :uuid="pageContent.uuid"
        :demo="pageContent.demo"
        class="content-page">
        <SharedPageBanner
          v-if="initialSavedPage.uuid"
          content-type-label="goal"
          :content-type="shareContentType"
          :user-name="initialSavedPage.user_name"
          :student-name="student.name"
          :last-updated="initialSavedPage.updated_at"
          :share-link="shareLink" />
        <div class="container">
          <p v-if="isLoading" class="lead text-muted"><i class="fa fa-circle-o-notch fa-spin"/> Loading...</p>
          <div class="row">
            <div class="col-sm-8">
              <h1>
                {{ initialSavedPage.title }}
              </h1>
              <div class="label-container">
                <span class="label label-lg label-success"><i class="fa fa-user"/> Goal in <span class="fs-block">{{ student.name }}</span></span>
              </div>

              <BigGoal
                :footer="pageContent.goal_example_text && pageContent.goal_example_text.length > 0 ? true : false"
                :header="goalHeaderText"
                :event-context="{ adapted_goal_level: 'grade' }"
              >
                <CopyTrackWrapper
                  slot="body"
                  :type="savedGoalContentType"
                  :title="initialSavedPage.title"
                  :level="'Grade Level'"
                  :uuid="initialSavedPage.uuid">
                  <WikiText
                    :text="initialSavedPage.grade_level_goal"
                    :strategies="strategyMapWithLibraryGoalQueryParams"
                  />
                </CopyTrackWrapper>

                <template v-slot:footer v-if="pageContent.goal_example_text">
                  <GoalExampleContent @toggle="clickedExampleContent">
                    <CopyTrackWrapper
                      type="goal-example"
                      :title="pageContent.title"
                      :uuid="pageContent.uuid"
                      :demo="pageContent.demo">
                      <WikiText :text="pageContent.goal_example_text" />
                    </CopyTrackWrapper>
                  </GoalExampleContent>
                </template>
              </BigGoal>

              <AdaptedGoalsAccordion
                v-if="adaptedGoalsArray"
                :goal-uuid="initialSavedPage.uuid"
                :goal-title="initialSavedPage.title"
                :subject="initialSavedPage.subject"
                :adapted-goals="adaptedGoalsArray"
                :strategies="strategyMapWithLibraryGoalQueryParams"
                :access-info="{}"
                :read-only="true"
                @adaptedGoal:paneltoggle="trackAdaptedGoalClick"
              />

              <h2>UDL-Aligned Strategies</h2>
              <UdlAdaptationPanelGroup
                :udl-sections="udlAdaptations"
                :locked="false"
                :strategies="strategyMapWithLibraryGoalQueryParams" />

            </div>
            <div class="col-sm-4">
              <template v-if="fetchingStandards || fetchingStaircase">
                <p class="text-muted"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</p>
              </template>
              <GoalContentPageStandards
                :content-profile-id="profileId"
                :grouped-standards="visibleGroupedStandardsBySetCode"
              />

              <template v-if="staircaseContent && staircaseContent.length > 0">
                <h2>Standard Staircase</h2>
                <CopyTrackWrapper
                  type="staircase"
                  :title="pageContent.title"
                  :uuid="pageContent.uuid"
                  :demo="pageContent.demo">
                  <Staircase
                    :grades="pageContent.grades"
                    :goal-staircase="staircaseContent"
                  />
                </CopyTrackWrapper>
              </template>

              <template v-if="strategies && strategies.length > 0">
                <h2>Referenced Strategies</h2>
                <StrategyMediaList :strategies="strategies" />
              </template>
            </div>
          </div>

          <TermsOfUseFooter />
        </div>

      </CopyTrackWrapper>
    </template>
  </div>
</template>

<script>
import {
  goalPageMixin,
  goalSetContentProfileMixin
} from '@/components/goals/common/goal_content_mixin'
import { sharedGoalContentMixin } from '@/components/goals/common/saved_goal_content_mixin'
import AdaptedGoalsAccordion from '@/components/goals/AdaptedGoalsAccordion'
import UdlAdaptationPanelGroup from '@/components/goals/UdlAdaptationPanelGroup'
import StrategyMediaList from '@/components/strategies/StrategyMediaList'
import SharedPageBanner from '@/components/SharedPageBanner'
import ShareLinkInvalidMessage from '@/components/library/ShareLinkInvalidMessage'

import { mapGetters, mapState } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'

export default {
  name: 'SharedGoalContentPage',
  mixins: [goalPageMixin, goalSetContentProfileMixin, sharedGoalContentMixin],
  components: {
    AdaptedGoalsAccordion,
    UdlAdaptationPanelGroup,
    StrategyMediaList,
    SharedPageBanner,
    ShareLinkInvalidMessage
  },
  props: {
    uuid: {
      required: true,
      type: String
    }
  },
  eventContext() {
    return {
      goal_uuid: this.pageContent.uuid,
      goal_title: this.pageContent.title,
      goal_grades: this.pageContent.grades,
      goal_subject: this.pageContent.subject,
      goal_subject_domains: this.pageContent.subject_domains,
      goal_standards:
        this.pageContent.standard_set_refs &&
        this.pageContent.standard_set_refs.map(setRef => setRef.standard_long_ref),
      goal_author_id: this.pageContent.author_id,
      goal_is_anchor: this.pageContent.super_page,
      goal_is_library: true,
      is_library: true,

      saved_goal_uuid: this.uuid,
      student_uuid: this.studentUuid
    }
  },
  data() {
    return {
      studentUuid: '',
      shareContentType: CONTENT_TYPES.SAVED_GOAL,
      showLinkExpiredMessage: false
    }
  },
  computed: {
    ...mapGetters('goaldetail', ['udlAdaptations']),
    ...mapState('goaldetail', ['strategies']),
    strategyMapWithLibraryGoalQueryParams() {
      let strategyMap = {}
      Object.keys(this.strategyMap).forEach(key => {
        let strategyInfo = this.strategyMap[key]
        strategyInfo.linkQueryParams = { lg: this.uuid }
        strategyMap[key] = strategyInfo
      })
      return strategyMap
    },
    shareLink() {
      return (
        window.location.origin +
        this.$router.resolve({
          name: 'SharedGoalContentPage',
          params: { uuid: this.uuid }
        }).href
      )
    }
  },
  methods: {
    async loadPageContent() {
      try {
        let savedGoal = await this.fetchLibraryGoal()
        this.studentUuid = savedGoal.student_uuid
        let ownersProfileId = savedGoal.user_content_profile_id
        if (ownersProfileId) {
          this.setActiveProfileById(ownersProfileId)
        }

        setTitle(`${savedGoal.title} | Shared Goal for ${savedGoal.student_name}`)

        await this.fetchPageContent({
          type: CONTENT_TYPES.GOAL,
          attribute: 'uuid',
          value: savedGoal.goal_uuid
        })
        this.$sendEvent('Viewed Shared Library Goal')
      } catch (err) {
        console.error(err)
        this.showLinkExpiredMessage = true
      }
    }
  },
  watch: {
    $route: function() {
      this.loadPageContent()
    }
  },
  created() {
    this.loadPageContent()
  },
  beforeDestroy() {
    this.setActiveProfileById(this.userContentProfileId)
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
@import '~@/mixins.less';
.label {
  padding: 0.3em 0.7em 0.4em;
  font-size: 85%;
}
.label-container {
  margin-bottom: 10px;
}
</style>
